/** Calls to the Workky API. Only public data crosses this boundary: embed key in, session token + session JWT out. */
export interface SessionStart {
  sessionId: string;
  sessionToken: string; // Anam session token (single session, short-lived)
  token: string; // Workky session JWT for follow-up calls
  maxSeconds: number;
  tenantName: string;
}

/** Public, per-embed-key appearance/config (GET /api/v1/embed/config). Empty object when unknown. */
export interface WidgetConfig {
  tenantName?: string | undefined;
  appearance?: {
    accent?: string | undefined;
    theme?: "auto" | "light" | "dark" | undefined;
    position?: "right" | "left" | undefined;
    launcherLabel?: string | undefined;
    poweredBy?: boolean | undefined;
  } | undefined;
}

export interface MediaCard {
  summary: string;
  facts: string[];
  text?: string;
  whenToShow: string;
}

export interface MediaInfo {
  id: string;
  kind: "image" | "video";
  mime: string;
  label: string;
  card: MediaCard;
  url: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`${status} ${code}`);
  }
}

async function call<T>(url: string, init: RequestInit): Promise<T> {
  const res = await fetch(url, { ...init, headers: { "Content-Type": "application/json", ...(init.headers ?? {}) } });
  if (!res.ok) {
    let code = "request_failed";
    try {
      code = ((await res.json()) as { error?: string }).error ?? code;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, code);
  }
  return (await res.json()) as T;
}

export function createApi(baseUrl: string, embedKey: string) {
  const base = baseUrl.replace(/\/$/, "");
  return {
    /** Appearance + tenant name for this key; never throws (defaults on any failure). */
    config: (): Promise<WidgetConfig> =>
      fetch(`${base}/api/v1/embed/config?key=${encodeURIComponent(embedKey)}`)
        .then((r) => (r.ok ? (r.json() as Promise<WidgetConfig>) : {}))
        .catch(() => ({})),

    /** Installation check: fire-and-forget, once per page load. */
    ping: () =>
      fetch(`${base}/api/v1/embed/ping`, { method: "POST", keepalive: true, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ embedKey, pageUrl: location.href }) }).catch(() => undefined),

    startSession: () =>
      call<SessionStart>(`${base}/api/v1/sessions`, { method: "POST", body: JSON.stringify({ embedKey, pageUrl: location.href }) }),

    resolveMedia: (s: SessionStart, mediaId: string) =>
      call<MediaInfo>(`${base}/api/v1/sessions/${s.sessionId}/media/${encodeURIComponent(mediaId)}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${s.token}` },
      }),

    describeMedia: (s: SessionStart, mediaId: string) =>
      call<{ id: string; kind: string; label: string; card: MediaCard }>(`${base}/api/v1/sessions/${s.sessionId}/media/${encodeURIComponent(mediaId)}/card`, {
        method: "GET",
        headers: { Authorization: `Bearer ${s.token}` },
      }),

    sendEvent: (s: SessionStart, type: string, data?: Record<string, string | number | boolean>) =>
      call<{ ok: true }>(`${base}/api/v1/sessions/${s.sessionId}/events`, {
        method: "POST",
        headers: { Authorization: `Bearer ${s.token}` },
        body: JSON.stringify({ type, data }),
      }).catch(() => undefined),

    /** keepalive so it survives page unload; token travels in the body because beacons can't set headers. */
    endSession: (s: SessionStart, reason: "user" | "cap" | "client_error", anamSessionId?: string) =>
      fetch(`${base}/api/v1/sessions/${s.sessionId}/end`, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: s.token, reason, anamSessionId }),
      }).catch(() => undefined),
  };
}

export type Api = ReturnType<typeof createApi>;
